import { TFunction } from 'i18next'
import dayjs from 'dayjs'
import CheckIcons from '@/icons/CheckIcons'
import LoadIcons from '@/icons/LoadIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import {
    DataTableAction,
    DataTableSelectionAction,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { FilterItemConfig } from '@/components/ui/data-table-customized'
import { getEntityConfig } from '@/components/Form/Shared/SimpleAsyncSelect/config'
import { DAY_OFF_STATUS, DAY_OFF_TYPE } from '../constants'
import { DayOffResponse, DayOffRangeTime } from '../types/response'

// ============================================================================
// Expanded Columns
// ============================================================================

export const getExpandedColumns = (
    t: TFunction
): DataTableColumn<DayOffRangeTime>[] => [
    {
        key: 'start_day_off',
        title: t('date'),
        cellClassName: 'capitalize',
        render: (value) => dayjs(value).format('dddd, DD/MM/YYYY'),
    },
    {
        key: 'day_off_option',
        title: t('session'),
        render: (value) =>
            value === 0
                ? t('morning')
                : value === 1
                  ? t('afternoon')
                  : t('all_day'),
    },
]

// ============================================================================
// HRM Actions
// ============================================================================

interface HrmActionsParams {
    t: TFunction
    setModal: (path: string, props: any) => void
    refetch: () => void
}

export const getHrmActions = ({
    t,
    setModal,
    refetch,
}: HrmActionsParams): DataTableAction<DayOffResponse>[] => [
    {
        key: 'leader-confirm',
        label: t('leader_confirm'),
        icon: <CheckIcons className="w-4 h-4" />,
        variant: 'info',
        onClick: (row) =>
            setModal('DayOffModals/LeaderConfirmModal/index', {
                dayOff: row,
                refetch,
            }),
        hidden: (row) => row?.day_off_status !== 'pending',
    },
    {
        key: 'bod-confirm',
        label: t('bod_confirm'),
        icon: <CheckIcons className="w-4 h-4" />,
        variant: 'success',
        onClick: (row) =>
            setModal('DayOffModals/BomConfirmModal/index', {
                dayOff: row,
                refetch,
            }),
        hidden: (row) => row?.day_off_status !== 'leader-approved',
    },
    {
        key: 'reject',
        label: t('reject'),
        icon: <DeleteOutlineIcons className="w-4 h-4" />,
        variant: 'danger',
        onClick: (row) =>
            setModal('DayOffModals/RejectModal/index', {
                dayOff: row,
                refetch,
            }),
        hidden: (row) => row?.day_off_status !== 'pending',
    },
]

// ============================================================================
// Member Actions
// ============================================================================

interface MemberActionsParams {
    t: TFunction
    navigate: (path: string) => void
    onCancel: (row: DayOffResponse) => void
}

export const getMemberActions = ({
    t,
    navigate,
    onCancel,
}: MemberActionsParams): DataTableAction<DayOffResponse>[] => [
    {
        key: 'edit',
        label: t('edit'),
        icon: <LoadIcons className="w-4 h-4" />,
        variant: 'info',
        onClick: (row) => navigate(`/account/dayoffs/${row?.day_off_id}/edit`),
        hidden: (row) => row?.day_off_status !== 'pending',
    },
    {
        key: 'cancel',
        label: t('cancel'),
        icon: <DeleteOutlineIcons className="w-4 h-4" />,
        variant: 'danger',
        onClick: onCancel,
        hidden: (row) => row?.day_off_status !== 'pending',
    },
]

// ============================================================================
// Selection Actions (HRM bulk approval)
// ============================================================================

interface SelectionActionsParams {
    t: TFunction
    setModal: (path: string, props: any) => void
    refetch: () => void
    clearSelection: () => void
}

export const getSelectionActions = ({
    t,
    setModal,
    refetch,
    clearSelection,
}: SelectionActionsParams): DataTableSelectionAction<DayOffResponse>[] => [
    {
        key: 'bulk-approve',
        label: t('multiple_approve'),
        icon: <CheckIcons className="w-4 h-4" />,
        variant: 'success',
        onClick: (selectedRows) => {
            setModal('DayOffModals/ApproveMultipleDayOffModal/index', {
                ids: selectedRows.map((row) => row.day_off_id),
                rows: selectedRows,
                refetch: () => {
                    refetch()
                    clearSelection()
                },
            })
        },
    },
]

// ============================================================================
// Filter Items
// ============================================================================

interface FilterItemsParams {
    t: TFunction
    pageType: 'member' | 'hrm'
    statusOptions: Array<{ label: string; value: string }>
    filters: { department_id: number }
    employeeActiveOnly: boolean
    setValue: (name: any, value: any) => void
    setFilters: (filters: any) => void
}

export const getFilterItems = ({
    t,
    pageType,
    statusOptions,
    filters,
    employeeActiveOnly,
    setValue,
    setFilters,
}: FilterItemsParams): FilterItemConfig[] => [
    {
        name: 'day_off_type',
        type: 'select',
        label: t('day_off_type'),
        placeholder: t('select_day_off_type'),
        toggleKey: 'showDayOffTypeFilter',
        options: DAY_OFF_TYPE?.map((item) => ({
            label: t(item.label),
            value: item.value,
        })),
    },
    {
        name: 'day_off_status',
        type: 'select',
        label: t('day_off_status'),
        placeholder: t('select_day_off_status'),
        toggleKey: 'showDayOffStatusFilter',
        options: statusOptions,
    },
    {
        name: 'department_id',
        type: 'department',
        enabled: pageType === 'hrm',
        toggleKey: 'showDepartmentFilter',
        onChange: (val: number) => {
            setValue('employee_ids', [])
            setFilters({ department_id: val, employee_ids: [] })
        },
    },
    {
        name: 'employee_ids',
        type: 'async-select',
        label: t('employee'),
        placeholder: t('select_employee'),
        enabled: pageType === 'hrm',
        toggleKey: 'showEmployeeFilter',
        multiple: true,
        asyncOptions: (query, page) =>
            getEntityConfig('employee').fetchOptionsWithPage(query, page || 1, {
                departmentIds: filters.department_id
                    ? [filters.department_id]
                    : undefined,
                activeOnly: employeeActiveOnly,
            }),
        getDefaultOptions: (ids) => getEntityConfig('employee').fetchByIds(ids),
        queryKey: `employee-${filters.department_id}-${employeeActiveOnly}`,
    },
    {
        name: 'day_off_item_date',
        type: 'daterangepicker',
        names: ['day_off_item_date', 'day_off_item_date_to'],
        label: t('day-off-item-date'),
        toggleKey: 'showDateRangeFilter',
        showPresets: true,
        clearable: true,
    },
]

// ============================================================================
// Filter Toggles (Settings panel)
// ============================================================================

export const getFilterToggles = (t: TFunction, pageType: 'member' | 'hrm') => [
    {
        key: 'showDayOffTypeFilter',
        label: t('show_day_off_type_filter'),
        type: 'visibility' as const,
        defaultValue: true,
    },
    {
        key: 'showDayOffStatusFilter',
        label: t('show_day_off_status_filter'),
        type: 'visibility' as const,
        defaultValue: true,
    },
    ...(pageType === 'hrm'
        ? [
              {
                  key: 'showDepartmentFilter',
                  label: t('show_department_filter'),
                  type: 'visibility' as const,
                  defaultValue: true,
              },
              {
                  key: 'showEmployeeFilter',
                  label: t('show_employee_filter'),
                  type: 'visibility' as const,
                  defaultValue: true,
              },
          ]
        : []),
    {
        key: 'showDateRangeFilter',
        label: t('show_date_range_filter'),
        type: 'visibility' as const,
        defaultValue: true,
    },
    ...(pageType === 'hrm'
        ? [
              {
                  key: 'employeeOptions',
                  label: t('employee_filter_mode', {
                      defaultValue: 'Nhân viên',
                  }),
                  type: 'options' as const,
                  singleSelect: true,
                  defaultSelectedOption: 'activeOnly',
                  options: [
                      {
                          label: t('active_employees_only'),
                          value: 'activeOnly',
                      },
                      {
                          label: t('all_employees', {
                              defaultValue: 'Toàn bộ NV',
                          }),
                          value: 'all',
                      },
                  ],
              },
          ]
        : []),
    {
        key: 'statusOptions',
        label: t('status_options_visible', {
            defaultValue: 'Trạng thái hiển thị',
        }),
        type: 'options' as const,
        options: DAY_OFF_STATUS.map((item) => ({
            label: t(item.label),
            value: item.value,
        })),
    },
]

// ============================================================================
// Selection Preview Columns
// ============================================================================

export const getPreviewColumns = (t: TFunction) => [
    { key: 'day_off_id', label: 'ID' },
    { key: 'employee_name', label: t('employee_name') },
    { key: 'department_name', label: t('department_name') },
    { key: 'position_name', label: t('position') },
    { key: 'number_of_day_off', label: t('days') },
    {
        key: 'day_off_type',
        label: t('day_off_type'),
        render: (value: string) => t(value),
    },
]
